// react
import {
	ChangeEvent,
	DragEvent,
	HTMLProps,
	MouseEvent,
	ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react';

// types
import { Option } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Box, Button, Grid, InputLabel, SelectChangeEvent, Typography } from '@mui/material';

// fields
import { Select } from '../Select/Select';
import { IconCircle } from '../../ui/IconCircle/IconCircle';

// utils
import { formatBytes } from '../../../utils/format';

// icons
import { DeleteOutlined as IconDelete } from '@mui/icons-material';

// props
export type FileEvent =
	| DragEvent<HTMLDivElement>
	| ChangeEvent<HTMLInputElement>
	| MouseEvent<HTMLButtonElement>;

export interface FilePickerProps extends Omit<
	HTMLProps<HTMLInputElement>,
	'defaultValue' | 'label' | 'onChange' | 'value'
> {
	defaultValue?: File[];
	helperText?: string;
	label?: ReactNode;
	maxFiles?: number;
	maxSize?: number;
	onChange?: (e: FileEvent, files: File[]) => void;
	onChangeDocType?: (e: SelectChangeEvent<unknown>, index: number) => void;
	onRemove?: (e: MouseEvent<HTMLButtonElement>, index: number) => void;
	optionsDocTypes?: Option[];
	orientation?: 'horizontal' | 'vertical';
	required?: boolean;
	value?: File[];
}

export interface FilterFilesParams {
	files: FileList | File[];
	isValid?: boolean;
}

export const fileErrors = ['FILE_INVALID_NAME_DOT', 'FILE_TOO_BIG'] as const;

export type FileError = (typeof fileErrors)[number];

export interface FilteredFiles {
	file: File;
	error?: FileError;
}

export const FilePicker = ({
	accept = '.csv, .doc, .docx, image/*, .pdf, .pptx, .rtf, .tar, .txt, .webp, .xls, .xlsx, .xml',
	defaultValue = [],
	disabled,
	helperText,
	id = 'files',
	label,
	maxFiles,
	maxSize = 400000000,
	multiple = true,
	name = 'files',
	onChange,
	onChangeDocType,
	onRemove,
	optionsDocTypes = [],
	orientation = 'vertical',
	required = false,
	value = [],
}: FilePickerProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [files, setFiles] = useState<File[]>(defaultValue || value);
	const [isDragOver, setIsDragOver] = useState(false);

	// refs
	const refFiles = useRef<HTMLInputElement>(null);

	const filterFiles = ({ files: filesSelected, isValid = true }: FilterFilesParams) => {
		const filesFiltered: FilteredFiles[] = [];

		// check size of each file
		for (let i = 0; i < filesSelected.length; i++) {
			const file = filesSelected[i];

			// check if files are valid
			if (isValid && file.size <= maxSize && file.name.split('.').length <= 2) {
				filesFiltered.push({ file });
			}
			// check if files are invalid
			if (!isValid && file.size > maxSize) {
				filesFiltered.push({ file, error: 'FILE_TOO_BIG' });
			} else if (!isValid && file.name.split('.').length > 2) {
				filesFiltered.push({ file, error: 'FILE_INVALID_NAME_DOT' });
			}
		}

		return filesFiltered;
	};

	const handleFiles = (e: FileEvent, filesSelected: FileList) => {
		// define all files
		const filesAll = [...files].concat(Array.from(filesSelected));

		// define valid files
		const filesValid = filterFiles({
			files: filesAll,
		});

		// pass valid files to onChange
		if (onChange) {
			onChange(
				e,
				filesValid.map((value) => value.file)
			);
		}

		// set files
		setFiles(filesAll);
	};

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		if (e.dataTransfer.files) {
			handleFiles(e, e.dataTransfer.files);
		}

		setIsDragOver(false);
	};

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(true);
	};

	const handleDragExit = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(false);
	};

	const handleRemoveFile = (e: MouseEvent<HTMLButtonElement>, index: number) => {
		// remove file by index
		const filesFilteredAll = files.filter((f, i) => f.name !== filesValid[index].file.name);

		const filesFilteredValid = filterFiles({
			files: filesFilteredAll,
		});

		// pass valid files to onChange
		if (onChange) {
			onChange(
				e,
				filesFilteredValid.map((value) => value.file)
			);
		}

		if (onRemove) {
			onRemove(e, index);
		}

		setFiles(filesFilteredAll);
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			handleFiles(e, e.target.files);
		}
	};

	const filesValid = filterFiles({
		files,
	});

	const filesInvalid = filterFiles({
		files,
		isValid: false,
	});

	const hasDocTypes = optionsDocTypes.length > 0;

	const errorTranslations: Record<FileError, string> = {
		FILE_INVALID_NAME_DOT: t('error.fileNameInvalidDot'),
		FILE_TOO_BIG: t('error.maxFileSize'),
	};

	const isVertical = orientation === 'vertical';

	let isDisabled = false;

	if (typeof disabled === 'boolean') {
		isDisabled = disabled;
	} else {
		if (!multiple || maxFiles) {
			if ((!multiple && files.length === 1) || files.length === maxFiles) {
				isDisabled = true;
			}
		}
	}

	useEffect(() => {
		if (value.length) {
			setFiles(value);
		}
	}, [value]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<InputLabel disabled={isDisabled} required={required}>
					{label || t('files')}
				</InputLabel>
				<Box
					onDragExit={handleDragExit}
					onDragOver={handleDragOver}
					onDrop={handleDrop}
					sx={(theme) => {
						return {
							transition: `all 200ms`,
							backgroundColor: isDragOver ? theme.palette.brand.grey['200'] : null,
							border: `1px ${theme.palette.brand.grey['400']} dashed`,
							borderRadius: 1.5,
							position: 'relative',
							padding: theme.spacing(3, 2),
							textAlign: isVertical ? 'center' : 'left',
						};
					}}>
					<Grid
						alignItems="center"
						container
						direction={isVertical ? 'column' : 'row'}
						justifyContent={isVertical ? 'center' : 'space-between'}
						spacing={1}
						sx={(theme) => {
							return {
								opacity: isDisabled ? 0.5 : 1,
							};
						}}>
						<Grid item>
							<Grid
								container
								alignItems="center"
								direction={isVertical ? 'column' : 'row'}
								spacing={1}>
								<Grid item>
									<IconCircle type="upload" />
								</Grid>
								<Grid item>
									<Typography fontWeight={600}>{t('uploadFiles')}</Typography>
									<Typography color="brand.grey.500" variant="caption">
										{formatBytes(maxSize)} {t('maxSize')}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<Button disabled={isDisabled} variant="outlined">
								{t('browseFiles')}
							</Button>
						</Grid>
					</Grid>
					<InputLabel
						disabled={isDisabled}
						htmlFor={id}
						style={{
							opacity: 0,
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							margin: 0,
						}}>
						{t('files')}
					</InputLabel>
					<input
						accept={accept}
						disabled={isDisabled}
						id={id}
						multiple={multiple}
						name={name}
						onChange={handleChangeInput}
						ref={refFiles}
						required={required && !files.length}
						style={{
							opacity: 0,
							pointerEvents: 'none',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
						}}
						type="file"
					/>
				</Box>
				{helperText && (
					<Typography color="brand.grey.500" variant="caption">
						{helperText}
					</Typography>
				)}
			</Grid>
			{filesValid.length > 0 && (
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								border: `1px ${theme.palette.brand.grey['400']} solid`,
								borderRadius: 1.5,
							};
						}}>
						<Grid container>
							{filesValid.map((value, i) => {
								const file = value.file;
								const isLast = i === filesValid.length - 1;
								return (
									<Grid item key={`file-${file.size}-${i}`} xs={12}>
										<Box
											sx={(theme) => {
												return {
													borderBottom: !isLast
														? `1px ${theme.palette.brand.grey['400']} solid`
														: null,
													padding: theme.spacing(2),
												};
											}}>
											<Grid
												alignItems="center"
												container
												justifyContent="space-between"
												spacing={2}>
												<Grid item xs={hasDocTypes ? 5 : 10}>
													<Typography fontWeight={600} variant="caption">
														{file.name}
													</Typography>
													<Typography color="brand.grey.500" component="div" variant="caption">
														{formatBytes(file.size)}
													</Typography>
												</Grid>
												<Grid item xs={hasDocTypes ? 7 : 2}>
													<Grid container justifyContent="flex-end" spacing={2}>
														{hasDocTypes && (
															<Grid item xs={9}>
																<Select
																	defaultValue={optionsDocTypes?.[0]?.value}
																	fullWidth={true}
																	name={`file-${i}-doc-type`}
																	onChange={(e) => {
																		if (onChangeDocType) {
																			onChangeDocType(e, i);
																		}
																	}}
																	options={optionsDocTypes}
																	size="small"
																/>
															</Grid>
														)}
														<Grid item xs="auto">
															<Button
																className="icon"
																onClick={(e) => handleRemoveFile(e, i)}
																sx={{ padding: `5px` }}
																variant="outlined">
																<IconDelete />
															</Button>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Box>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				</Grid>
			)}
			{filesInvalid.length > 0 && (
				<Grid item xs={12}>
					<Alert
						onClose={() => {
							// remove invalid files
							setFiles(filesValid.map((value) => value.file));
						}}
						severity="error">
						{fileErrors
							// filter errors that have invalid files
							.filter((error) => filesInvalid.some((value) => value.error === error))
							.map((error) => {
								const filesForError = filesInvalid.filter((value) => value.error === error);

								return (
									<Box key={error}>
										<Typography>{errorTranslations[error]}</Typography>
										<Box
											component="ul"
											sx={(theme) => {
												return {
													margin: theme.spacing(1, 0, 0, 0),
												};
											}}>
											{filesForError.map((value) => {
												return <li key={`${error}-${value.file.name}`}>{value.file.name}</li>;
											})}
										</Box>
									</Box>
								);
							})}
					</Alert>
				</Grid>
			)}
		</Grid>
	);
};
